import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, User, Mail, Phone, Loader2, Check, Lock, AlertCircle, Clock, ChevronLeft, ChevronRight, LayoutGrid, List, PlusCircle, X, Sparkles, CreditCard, Landmark, Banknote, Copy } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useClientAuth } from "../context/ClientAuthContext";

// Upsell map — for a given pack_id, suggest a complementary pack
const UPSELL_MAP = {
  // EMS → propose a Drain & Sculpt or Drainage
  decouverte: "drainage_ventre_jambes",
  pack_5: "drainsculpt_haut",
  pack_10: "drainsculpt_corps_entier",
  pack_20: "drainsculpt_corps_entier",
  duo_ems_massage: "drainage_corps_entier",

  // Drainage Lymphatique → propose Drain & Sculpt complémentaire
  drainage_ventre_jambes: "drainsculpt_haut",
  drainage_corps_entier: "drainsculpt_corps_entier",
  drainage_cure5: "drainsculpt_cure5",

  // Post-op → propose drainage léger
  postop_seance: "drainage_ventre_jambes",
  postop_pack8: "drainage_corps_entier",

  // Drain & Sculpt → propose la zone complémentaire
  drainsculpt_bas: "drainsculpt_haut",
  drainsculpt_haut: "drainsculpt_bas",
  drainsculpt_corps_entier: "drainage_corps_entier",
  drainsculpt_cure5: "drainage_cure5",
};

const DAY_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const DAY_LABELS_FULL = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const MONTH_LABELS = ["Janv.", "Févr.", "Mars", "Avril", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
const MONTH_LABELS_FULL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function isoFromDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Reservation() {
  const [searchParams] = useSearchParams();
  const { client, ready } = useClientAuth();
  const [packs, setPacks] = useState([]);
  const [slotsAvail, setSlotsAvail] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]); // array of slot ids
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card"); // 'card'|'bank_transfer'|'on_site'
  const [confirmation, setConfirmation] = useState(null); // non-card confirmation payload
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
  const [form, setForm] = useState({
    pack_id: searchParams.get("pack") || "",
    addon_pack_id: "",
    objectif: "remise_en_forme",
    adresse: "",
    code_postal: "",
    ville: "Istres",
    notes: "",
    use_credit_impot: true,
  });

  useEffect(() => {
    api.get("/packs").then((r) => {
      setPacks(r.data);
      if (!form.pack_id && r.data.length > 0) {
        const popular = r.data.find((p) => p.popular) || r.data[0];
        setForm((f) => ({ ...f, pack_id: popular.id }));
      }
    });
    api.get("/availability")
      .then((r) => setSlotsAvail(r.data))
      .finally(() => setLoadingSlots(false));
    api.get("/payment-methods")
      .then((r) => {
        setPaymentMethods(r.data);
        // default to first enabled method
        if (r.data?.card) setPaymentMethod("card");
        else if (r.data?.bank_transfer?.enabled) setPaymentMethod("bank_transfer");
        else if (r.data?.on_site?.enabled) setPaymentMethod("on_site");
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill address from client
  useEffect(() => {
    if (client) {
      setForm((f) => ({
        ...f,
        adresse: f.adresse || client.adresse || "",
        code_postal: f.code_postal || client.code_postal || "",
        ville: f.ville || client.ville || "Istres",
      }));
    }
  }, [client]);

  const selectedPack = useMemo(() => packs.find((p) => p.id === form.pack_id), [packs, form.pack_id]);
  const suggestedAddonId = selectedPack ? UPSELL_MAP[selectedPack.id] : null;
  const suggestedAddon = useMemo(
    () => packs.find((p) => p.id === suggestedAddonId),
    [packs, suggestedAddonId]
  );
  const addonPack = useMemo(
    () => (form.addon_pack_id ? packs.find((p) => p.id === form.addon_pack_id) : null),
    [packs, form.addon_pack_id]
  );
  const requiredSlots = selectedPack?.nb_seances || 1;
  const basePrice = selectedPack ? selectedPack.price : 0;
  const addonPrice = addonPack ? addonPack.price : 0;
  const totalPrice = basePrice + addonPrice;
  const finalPrice = form.use_credit_impot ? totalPrice * 0.5 : totalPrice;

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = {};
    slotsAvail.forEach((s) => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [slotsAvail]);

  const sortedDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset selected slots & addon when pack changes
  useEffect(() => {
    setSelectedSlots([]);
    setForm((f) => ({ ...f, addon_pack_id: "" }));
  }, [form.pack_id]);

  const toggleSlot = (slotId) => {
    setSelectedSlots((prev) => {
      if (prev.includes(slotId)) return prev.filter((id) => id !== slotId);
      if (prev.length >= requiredSlots) {
        toast.warning(`Vous ne pouvez sélectionner que ${requiredSlots} créneau(x) pour ce pack`);
        return prev;
      }
      return [...prev, slotId];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!client) {
      toast.error("Veuillez vous connecter avant de finaliser votre réservation");
      return;
    }
    if (!form.pack_id) {
      toast.error("Veuillez sélectionner un pack");
      return;
    }
    if (selectedSlots.length !== requiredSlots) {
      toast.error(`Sélectionnez exactement ${requiredSlots} créneau(x)`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        pack_id: form.pack_id,
        addon_pack_id: form.addon_pack_id || null,
        client_id: client.id,
        slot_ids: selectedSlots,
        prenom: client.prenom,
        nom: client.nom,
        email: client.email,
        fiscal_email: client.fiscal_email || null,
        urssaf_consent: !!client.urssaf_consent,
        telephone: client.telephone,
        objectif: form.objectif,
        adresse: form.adresse,
        code_postal: form.code_postal,
        ville: form.ville,
        notes: form.notes,
        use_credit_impot: form.use_credit_impot,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
      };
      const { data } = await api.post("/reservations/checkout", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.payment_method === "bank_transfer" || data.payment_method === "on_site") {
        setConfirmation(data);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error("Réponse de paiement inattendue");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return `${DAY_LABELS[d.getDay()]} ${d.getDate()} ${MONTH_LABELS[d.getMonth()]}`;
  };

  return (
    <div className="bg-white">
      {confirmation && (
        <ReservationConfirmation data={confirmation} onClose={() => { setConfirmation(null); setLoading(false); }} />
      )}
      <section className="bg-fegray-light pt-16 pb-12 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-4">
            — Réserver en ligne
          </p>
          <h1 className="text-4xl lg:text-5xl font-bold text-fegray-dark">
            Votre séance, <span className="text-fegreen">en 3 étapes</span>
          </h1>
          <p className="mt-5 text-fegray max-w-xl mx-auto">
            Sélectionnez votre formule, choisissez vos créneaux et confirmez avec un paiement
            sécurisé Stripe.
          </p>
        </div>
      </section>

      {ready && !client && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
          <LoginBanner />
        </div>
      )}

      <form onSubmit={submit} data-testid="reservation-form">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-12">
            {/* Step 1 — Pack */}
            <div>
              <h2 className="text-xl font-bold text-fegray-dark mb-2">
                <span className="text-fegreen">01.</span> Votre formule
              </h2>
              <p className="text-sm text-fegray mb-6">Sélectionnez le pack adapté à vos objectifs.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {packs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`reserve-pack-${p.id}`}
                    onClick={() => set("pack_id", p.id)}
                    className={`text-left fe-card p-5 transition-all ${
                      form.pack_id === p.id ? "ring-2 ring-fegreen" : ""
                    }`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <div className="font-bold text-fegray-dark">{p.name}</div>
                        <div className="text-xs text-fegray mt-1">
                          {p.nb_seances} séance{p.nb_seances > 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-extrabold text-fegreen">{p.price}€</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* UPSELL — suggested addon */}
              {suggestedAddon && (
                <UpsellCard
                  addon={suggestedAddon}
                  selected={form.addon_pack_id === suggestedAddon.id}
                  useCredit={form.use_credit_impot}
                  onToggle={() =>
                    setForm((f) => ({
                      ...f,
                      addon_pack_id: f.addon_pack_id === suggestedAddon.id ? "" : suggestedAddon.id,
                    }))
                  }
                />
              )}
            </div>

            {/* Step 2 — Créneaux */}
            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h2 className="text-xl font-bold text-fegray-dark">
                  <span className="text-fegreen">02.</span> Vos créneaux
                </h2>
                <span className="text-sm font-semibold text-fegreen" data-testid="slots-counter">
                  {selectedSlots.length} / {requiredSlots} sélectionné{selectedSlots.length > 1 ? "s" : ""}
                </span>
              </div>
              <p className="text-sm text-fegray mb-4">
                Choisissez {requiredSlots > 1 ? `les ${requiredSlots} créneaux` : "le créneau"} qui vous conviennent
                {requiredSlots > 1 ? " — vous pouvez répartir vos séances sur plusieurs semaines." : "."}
              </p>

              {/* View toggle */}
              <div className="inline-flex items-center gap-1 bg-white rounded-full p-1 border border-gray-200 mb-5">
                <button
                  type="button"
                  onClick={() => setViewMode("calendar")}
                  data-testid="view-calendar"
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                    viewMode === "calendar" ? "bg-fegreen text-white shadow" : "text-fegray hover:text-fegray-dark"
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Vue mois
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  data-testid="view-list"
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                    viewMode === "list" ? "bg-fegreen text-white shadow" : "text-fegray hover:text-fegray-dark"
                  }`}
                >
                  <List className="w-3.5 h-3.5" /> Vue liste
                </button>
              </div>

              {loadingSlots ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-fegreen" />
                </div>
              ) : sortedDates.length === 0 ? (
                <div className="fe-card p-6 text-center">
                  <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm text-fegray-dark">Aucun créneau disponible pour le moment.</p>
                  <p className="text-xs text-fegray mt-1">
                    Contactez Rosalie au <a href="tel:+33766253143" className="text-fegreen font-semibold">07 66 25 31 43</a> pour réserver.
                  </p>
                </div>
              ) : viewMode === "calendar" ? (
                <CalendarView
                  currentMonth={currentMonth}
                  setCurrentMonth={setCurrentMonth}
                  slotsByDate={slotsByDate}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  selectedSlots={selectedSlots}
                  toggleSlot={toggleSlot}
                />
              ) : (
                <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2 fe-scroll">
                  {sortedDates.map((date) => (
                    <div key={date} className="fe-card p-4" data-testid={`day-${date}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4 text-fegreen" />
                        <h3 className="font-bold text-fegray-dark text-sm capitalize">{fmtDate(date)}</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {slotsByDate[date].map((s) => {
                          const active = selectedSlots.includes(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => toggleSlot(s.id)}
                              data-testid={`slot-${s.id}`}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all border ${
                                active
                                  ? "bg-fegreen text-white border-fegreen shadow-md"
                                  : "bg-white text-fegray-dark border-gray-200 hover:border-fegreen hover:text-fegreen"
                              }`}
                            >
                              {active && <Check className="w-3 h-3" />}
                              <Clock className="w-3 h-3" /> {s.time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 3 — Coordonnées + adresse */}
            <div>
              <h2 className="text-xl font-bold text-fegray-dark mb-2">
                <span className="text-fegreen">03.</span> Adresse de séance
              </h2>
              <p className="text-sm text-fegray mb-6">
                Rosalie se déplace dans tout le bassin Istres / Fos-sur-Mer.
              </p>

              {client && (
                <div className="fe-card p-4 mb-5 bg-fegreen-light/40 border border-fegreen/20">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-fegreen text-white font-bold text-sm flex items-center justify-center">
                        {(client.prenom?.[0] || "") + (client.nom?.[0] || "")}
                      </span>
                      <div>
                        <div className="text-sm font-bold text-fegray-dark">{client.prenom} {client.nom}</div>
                        <div className="text-xs text-fegray">{client.email} · {client.telephone}</div>
                      </div>
                    </div>
                    <span className="text-xs text-fegreen font-semibold uppercase tracking-wider">Connecté</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SelectField
                  label="Objectif principal"
                  name="objectif"
                  value={form.objectif}
                  onChange={set}
                  className="md:col-span-3"
                  options={[
                    { v: "reprise_activite", l: "Reprise d'activité" },
                    { v: "remise_en_forme", l: "Remise en forme" },
                    { v: "sante_bien_etre", l: "Santé & Bien-être" },
                    { v: "sport_performance", l: "Sport & Performance" },
                  ]}
                />
                <div className="md:col-span-3">
                  <Field icon={MapPin} label="Adresse" name="adresse" value={form.adresse} onChange={set} required />
                </div>
                <Field label="Code postal" name="code_postal" value={form.code_postal} onChange={set} required />
                <div className="md:col-span-2">
                  <Field label="Ville" name="ville" value={form.ville} onChange={set} required />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs uppercase tracking-wider text-fegray mb-2">Notes (optionnel)</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Étage, code d'accès, particularités..."
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none transition"
                    data-testid="reserve-notes"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-4">
            <div className="fe-card p-7 sticky top-40">
              <p className="text-xs uppercase tracking-wider text-fegreen font-bold mb-4">Récapitulatif</p>

              {selectedPack ? (
                <>
                  <div className="pb-5 border-b border-gray-100">
                    <div className="font-bold text-fegray-dark text-lg">{selectedPack.name}</div>
                    <div className="text-sm text-fegray mt-1">
                      {selectedPack.nb_seances} séance{selectedPack.nb_seances > 1 ? "s" : ""}
                    </div>
                  </div>

                  {addonPack && (
                    <div className="mt-4 p-3 rounded-xl bg-fegreen-light/40 border border-fegreen/20 flex justify-between items-start gap-3" data-testid="summary-addon">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-fegreen font-bold flex items-center gap-1 mb-0.5">
                          <PlusCircle className="w-3 h-3" /> Ajout
                        </div>
                        <div className="text-sm font-bold text-fegray-dark truncate">{addonPack.name}</div>
                        <div className="text-xs text-fegreen mt-0.5">+ {addonPack.price}€</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => set("addon_pack_id", "")}
                        data-testid="summary-remove-addon"
                        aria-label="Retirer le service additionnel"
                        className="w-6 h-6 rounded-full bg-white hover:bg-red-50 border border-gray-200 text-fegray-dark hover:text-red-500 flex items-center justify-center flex-shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <div className="mt-4 space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-fegray font-semibold">Créneaux choisis</p>
                    {selectedSlots.length === 0 ? (
                      <p className="text-xs text-fegray italic">Aucun créneau sélectionné</p>
                    ) : (
                      selectedSlots.map((id) => {
                        const slot = slotsAvail.find((s) => s.id === id);
                        if (!slot) return null;
                        return (
                          <div key={id} className="text-xs text-fegray-dark flex items-center gap-2">
                            <Check className="w-3 h-3 text-fegreen" />
                            {fmtDate(slot.date)} · {slot.time}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <label className="flex items-start gap-3 mt-5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.use_credit_impot}
                      onChange={(e) => set("use_credit_impot", e.target.checked)}
                      data-testid="reserve-credit-checkbox"
                      className="mt-1 accent-fegreen w-4 h-4"
                    />
                    <span className="text-sm text-fegray-dark">
                      <span className="font-bold">Crédit d'impôt 50%</span>
                      <span className="block text-xs text-fegray mt-1">
                        Avance immédiate URSSAF appliquée
                      </span>
                    </span>
                  </label>

                  <div className="mt-5 space-y-2 pt-5 border-t border-gray-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-fegray">{selectedPack.name}</span>
                      <span className="text-fegray-dark font-medium">{basePrice}€</span>
                    </div>
                    {addonPack && (
                      <div className="flex justify-between text-sm">
                        <span className="text-fegray truncate pr-2">+ {addonPack.name}</span>
                        <span className="text-fegray-dark font-medium">{addonPrice}€</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-50">
                      <span className="text-fegray">Sous-total</span>
                      <span className="text-fegray-dark font-medium">{totalPrice}€</span>
                    </div>
                    {form.use_credit_impot && (
                      <div className="flex justify-between text-sm text-fegreen">
                        <span>Crédit d'impôt 50%</span>
                        <span>− {(totalPrice * 0.5).toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-gray-100">
                      <span className="font-bold text-fegray-dark uppercase tracking-wider text-sm">À payer</span>
                      <span className="text-3xl font-extrabold text-fegreen" data-testid="total-price">
                        {finalPrice.toFixed(2)}€
                      </span>
                    </div>
                  </div>

                  {client && paymentMethods && (
                    <div className="mt-6">
                      <p className="text-[10px] uppercase tracking-wider text-fegray font-bold mb-2">Moyen de paiement</p>
                      <div className="space-y-2" data-testid="payment-method-selector">
                        {paymentMethods.card && (
                          <PaymentMethodOption
                            value="card"
                            current={paymentMethod}
                            onSelect={setPaymentMethod}
                            icon={CreditCard}
                            label="Carte bancaire"
                            sub="Paiement sécurisé Stripe — vous serez redirigé."
                          />
                        )}
                        {paymentMethods.bank_transfer?.enabled && (
                          <PaymentMethodOption
                            value="bank_transfer"
                            current={paymentMethod}
                            onSelect={setPaymentMethod}
                            icon={Landmark}
                            label="Virement bancaire"
                            sub="Les coordonnées vous seront envoyées après confirmation."
                          />
                        )}
                        {paymentMethods.on_site?.enabled && (
                          <PaymentMethodOption
                            value="on_site"
                            current={paymentMethod}
                            onSelect={setPaymentMethod}
                            icon={Banknote}
                            label="Sur place"
                            sub="Réglez Rosalie en espèces ou par chèque lors de la séance."
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {!client ? (
                    <Link
                      to="/account/login"
                      state={{ from: "/reservation" }}
                      className="btn-green w-full mt-7"
                      data-testid="reserve-login-cta"
                    >
                      <Lock className="w-4 h-4" /> Connectez-vous pour réserver
                    </Link>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading || selectedSlots.length !== requiredSlots}
                      data-testid="submit-reservation"
                      className="btn-green w-full mt-7 disabled:opacity-50"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {paymentMethod === "card" ? "Redirection…" : "Confirmation…"}</>
                      ) : paymentMethod === "card" ? (
                        <>Payer & réserver <ArrowRight className="w-4 h-4" /></>
                      ) : paymentMethod === "bank_transfer" ? (
                        <>Confirmer & recevoir les coordonnées <ArrowRight className="w-4 h-4" /></>
                      ) : (
                        <>Confirmer la réservation <ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>
                  )}
                  <p className="text-[10px] text-fegray/60 mt-3 text-center uppercase tracking-wider">
                    {paymentMethod === "card"
                      ? "Paiement sécurisé Stripe"
                      : paymentMethod === "bank_transfer"
                        ? "Créneau bloqué dès confirmation · 72h pour régler"
                        : "Créneau bloqué dès confirmation"}
                  </p>
                </>
              ) : (
                <p className="text-fegray">Sélectionnez un pack pour afficher le récapitulatif.</p>
              )}
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
}

function LoginBanner() {
  return (
    <div className="fe-card p-5 lg:p-6 bg-fegreen-light/50 border-2 border-fegreen/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-fegreen mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-bold text-fegray-dark text-sm">Réservation réservée aux clients connectés</p>
          <p className="text-xs text-fegray mt-1">
            Créez votre compte (ou connectez-vous) pour bénéficier de l'avance URSSAF immédiate
            et gérer votre historique de séances.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Link
          to="/account/login"
          state={{ from: "/reservation" }}
          className="px-4 py-2.5 rounded-full bg-fegray-dark text-white text-sm font-semibold hover:bg-fegreen transition-colors"
          data-testid="banner-login"
        >
          Connexion
        </Link>
        <Link
          to="/account/register"
          state={{ from: "/reservation" }}
          className="btn-green text-sm !py-2.5"
          data-testid="banner-register"
        >
          Créer un compte
        </Link>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2">
        {label}{required && <span className="text-fegreen"> *</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fegray/50" />}
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          data-testid={`reserve-${name}`}
          className={`w-full bg-white border border-gray-200 rounded-xl ${Icon ? "pl-10" : "pl-4"} pr-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none transition`}
        />
      </div>
    </div>
  );
}

function UpsellCard({ addon, selected, useCredit, onToggle }) {
  const finalAddonPrice = useCredit ? (addon.price * 0.5).toFixed(2) : addon.price.toFixed(2);
  return (
    <div
      className={`mt-5 rounded-2xl border-2 p-5 lg:p-6 transition-all ${
        selected
          ? "bg-fegreen-light/50 border-fegreen shadow-md"
          : "bg-white border-dashed border-fegreen/40 hover:border-fegreen hover:bg-fegreen-light/20"
      }`}
      data-testid="upsell-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            selected ? "bg-fegreen text-white" : "bg-fegreen-light text-fegreen"
          }`}
        >
          {selected ? <Check className="w-6 h-6" strokeWidth={3} /> : <Sparkles className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-fegreen font-extrabold mb-1">
            {selected ? "Service ajouté" : "Recommandé pour vous"}
          </p>
          <div className="font-bold text-fegray-dark text-base sm:text-lg">
            Complétez avec : <span className="text-fegreen">{addon.name}</span>
          </div>
          <p className="text-xs sm:text-sm text-fegray mt-1">{addon.description}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-fegray">+{addon.price}€</div>
          {useCredit && (
            <div className="text-[10px] text-fegreen font-semibold">soit {finalAddonPrice}€ net</div>
          )}
          <button
            type="button"
            onClick={onToggle}
            data-testid={selected ? "upsell-remove" : "upsell-add"}
            className={`mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
              selected
                ? "bg-fegray-dark text-white hover:bg-red-500"
                : "bg-fegreen text-white hover:bg-fegreen/90 shadow-md"
            }`}
          >
            {selected ? (
              <><X className="w-3 h-3" /> Retirer</>
            ) : (
              <><PlusCircle className="w-3 h-3" /> Ajouter</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


function SelectField({ label, name, value, onChange, options, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`reserve-${name}`}
        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none transition"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </div>
  );
}


function CalendarView({ currentMonth, setCurrentMonth, slotsByDate, selectedDay, setSelectedDay, selectedSlots, toggleSlot }) {
  const todayIso = isoFromDate(new Date());

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  // Convert Sunday=0 to Monday=0 grid (FR week starts Monday)
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  // Leading blanks
  for (let i = 0; i < startOffset; i++) cells.push(null);
  // Days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(isoFromDate(new Date(year, month, d)));
  }
  // Trailing blanks to fill rows of 7
  while (cells.length % 7 !== 0) cells.push(null);

  const goPrev = () => setCurrentMonth(new Date(year, month - 1, 1));
  const goNext = () => setCurrentMonth(new Date(year, month + 1, 1));

  const selectedDaySlots = selectedDay ? (slotsByDate[selectedDay] || []) : [];

  const fmtFullDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return `${DAY_LABELS_FULL[d.getDay()]} ${d.getDate()} ${MONTH_LABELS_FULL[d.getMonth()]}`;
  };

  return (
    <div className="space-y-4" data-testid="calendar-view">
      {/* Month header */}
      <div className="fe-card p-4 lg:p-5">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goPrev}
            data-testid="cal-prev"
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-fegreen hover:text-fegreen transition-colors text-fegray-dark"
            aria-label="Mois précédent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="font-bold text-fegray-dark text-base lg:text-lg capitalize" data-testid="cal-month-label">
            {MONTH_LABELS_FULL[month]} {year}
          </h3>
          <button
            type="button"
            onClick={goNext}
            data-testid="cal-next"
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:border-fegreen hover:text-fegreen transition-colors text-fegray-dark"
            aria-label="Mois suivant"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
            <div key={d} className="text-center text-[10px] uppercase tracking-wider text-fegray font-bold py-1.5">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`b-${i}`} className="aspect-square" />;
            const slots = slotsByDate[iso] || [];
            const hasSlots = slots.length > 0;
            const isPast = iso < todayIso;
            const isSelected = selectedDay === iso;
            const selectedCount = slots.filter((s) => selectedSlots.includes(s.id)).length;
            const isToday = iso === todayIso;

            return (
              <button
                key={iso}
                type="button"
                disabled={isPast || !hasSlots}
                onClick={() => setSelectedDay(iso)}
                data-testid={`cal-day-${iso}`}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-semibold relative transition-all ${
                  isSelected
                    ? "bg-fegreen text-white shadow-md ring-2 ring-fegreen ring-offset-1"
                    : hasSlots && !isPast
                    ? "bg-fegreen-light/40 text-fegray-dark hover:bg-fegreen-light hover:text-fegreen border border-fegreen/20"
                    : isPast
                    ? "text-fegray/30 cursor-not-allowed"
                    : "text-fegray/40 cursor-not-allowed"
                } ${isToday && !isSelected ? "ring-1 ring-fegray-dark/30" : ""}`}
              >
                <span className="leading-none">{parseInt(iso.split("-")[2], 10)}</span>
                {hasSlots && !isPast && (
                  <span className={`text-[9px] mt-1 font-bold ${isSelected ? "text-white/90" : "text-fegreen"}`}>
                    {slots.length} dispo
                  </span>
                )}
                {selectedCount > 0 && !isSelected && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-fegreen text-white text-[9px] font-extrabold flex items-center justify-center shadow">
                    {selectedCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-[10px] text-fegray">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-fegreen-light/60 border border-fegreen/20" /> Disponible
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-fegreen" /> Sélectionné
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-gray-100" /> Indisponible
          </span>
        </div>
      </div>

      {/* Slots panel for selected day */}
      {selectedDay ? (
        <div className="fe-card p-5 lg:p-6" data-testid="calendar-slots-panel">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-fegreen" />
            <h3 className="font-bold text-fegray-dark text-sm capitalize">{fmtFullDate(selectedDay)}</h3>
            <span className="ml-auto text-xs text-fegray">
              {selectedDaySlots.length} créneau{selectedDaySlots.length > 1 ? "x" : ""}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedDaySlots.map((s) => {
              const active = selectedSlots.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSlot(s.id)}
                  data-testid={`slot-${s.id}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-xs font-bold transition-all border ${
                    active
                      ? "bg-fegreen text-white border-fegreen shadow-md"
                      : "bg-white text-fegray-dark border-gray-200 hover:border-fegreen hover:text-fegreen"
                  }`}
                >
                  {active && <Check className="w-3 h-3" />}
                  <Clock className="w-3 h-3" /> {s.time}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="fe-card p-6 text-center text-sm text-fegray">
          <Calendar className="w-5 h-5 text-fegreen mx-auto mb-2" />
          Cliquez sur un jour disponible pour afficher ses créneaux.
        </div>
      )}
    </div>
  );
}

function PaymentMethodOption({ value, current, onSelect, icon: Icon, label, sub }) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={`pay-method-${value}`}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border-2 transition-colors ${
        active ? "border-fegreen bg-fegreen-light/30" : "border-gray-200 hover:border-fegreen/50"
      }`}
    >
      <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${
        active ? "bg-fegreen text-white" : "bg-fegray-light text-fegreen"
      }`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-fegray-dark text-sm">{label}</span>
        <span className="block text-[11px] text-fegray leading-snug mt-0.5">{sub}</span>
      </span>
      <span className={`w-4 h-4 rounded-full border-2 mt-1 shrink-0 transition-colors ${
        active ? "border-fegreen bg-fegreen" : "border-gray-300"
      }`} />
    </button>
  );
}

function ReservationConfirmation({ data, onClose }) {
  const isBank = data.payment_method === "bank_transfer";
  const bd = data.bank_details || {};
  const amount = data.amount_after_credit || data.amount || 0;
  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(bd.iban || "");
      toast.success("IBAN copié");
    } catch { toast.error("Copie impossible"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="reservation-confirmation">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 relative overflow-y-auto max-h-[90vh]">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center">
          <X className="w-4 h-4 text-fegray-dark" />
        </button>
        <div className="w-14 h-14 rounded-full bg-fegreen-light flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-fegreen" strokeWidth={2.5} />
        </div>
        <h3 className="text-2xl font-extrabold text-fegray-dark text-center mb-2">
          Réservation confirmée
        </h3>
        <p className="text-sm text-fegray text-center mb-5 leading-relaxed">
          Votre créneau est bloqué. {isBank
            ? "Réglez le montant ci-dessous par virement dans les 72h pour valider définitivement."
            : "Réglez Rosalie en espèces ou par chèque lors de la première séance."}
        </p>

        <div className="bg-fegray-light rounded-2xl p-5 mb-5">
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-xs text-fegray uppercase tracking-wider font-bold">Montant à régler</span>
            <span className="text-2xl font-extrabold text-fegreen">{Number(amount).toFixed(2)}€</span>
          </div>
        </div>

        {isBank ? (
          <div className="space-y-3 mb-5">
            <DetailRow label="Bénéficiaire" value={bd.holder || "—"} />
            <DetailRow label="IBAN" value={bd.iban || "—"} mono action={<button onClick={copyIban} className="text-fegreen text-[10px] font-bold uppercase tracking-wider hover:underline inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copier</button>} />
            <DetailRow label="BIC / SWIFT" value={bd.bic || "—"} mono />
            <DetailRow label="Référence à indiquer" value={bd.reference || "—"} highlight />
          </div>
        ) : (
          <div className="bg-fegreen-light/40 border border-fegreen/20 rounded-xl p-4 mb-5 text-sm text-fegray-dark leading-relaxed">
            <strong className="text-fegreen">Paiement à la séance.</strong>
            <br />
            Pensez à apporter votre règlement (espèces, chèque à l&apos;ordre de Rosalie) lors du premier rendez-vous.
          </div>
        )}

        <Link
          to="/account/login"
          className="btn-green w-full"
          data-testid="confirmation-go-account"
        >
          Voir mes réservations <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-[11px] text-fegray text-center mt-3 leading-relaxed">
          Une question ? Appelez Rosalie au <a href="tel:+33766253143" className="text-fegreen font-bold">07 66 25 31 43</a>.
        </p>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono, action, highlight }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${highlight ? "bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" : ""}`}>
      <span className="text-[11px] text-fegray uppercase tracking-wider font-bold shrink-0">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-sm text-fegray-dark font-semibold truncate ${mono ? "font-mono" : ""}`}>{value}</span>
        {action}
      </div>
    </div>
  );
}
